import React, { useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import { authRoutes } from "v2/routes/routesGroups/authRoutes";

import { ApplicationAsset, AssetType, Status } from "api/models";
import {
  updateReferenceVariation as updateReferenceVariationMutation,
  UpdateReferenceVariationResponse,
} from "v2/api/mutations/application/updateReferenceVariation";

import { UPDATE_CONTROL_PROMO_ART } from "api/mutations/assets";
import {
  createPromoArts,
  deactivatePromoArt,
  DeactivatePromoArtResponse,
  deletePromoArt,
  DeletePromoArtResponse,
} from "v2/api/mutations/application/promoArts";

import { GET_APPLICATION_PROMO_ARTS } from "api/queries/assets";
import { ApplicationContext, ModalContext } from "contexts";

import { getTrafficIndicator } from "utils/statistics";
import { useFullPageLoader } from "v2/hooks/useFullPageLoader";

import { runAction } from "../helpers/runAction";
import { updateReferenceVariation } from "../helpers/updateReferenceVariation";

import Page from "./ApplicationElementsPromoArtsPage";
import { Spinner, Error } from "components";
import ApplicationElements from "screens/Application/ApplicationElements/components/ApplicationElementsContainer";
import { useHasUserPermission } from "v2/hooks/useHasUserPermission";

interface Props extends RouteComponentProps<{ id: string }> {}

function ApplicationElementsPromoArtsContainer({ history, match }: Props) {
  const hasUserPermissions = useHasUserPermission();

  const applicationId = match.params.id;
  const { data, error, loading } = useQuery(GET_APPLICATION_PROMO_ARTS, {
    variables: { applicationId },
    fetchPolicy: "network-only",
  });

  const [fullPageLoader, setFullPageLoader] = useFullPageLoader();

  const [deleteMutation] = useMutation<DeletePromoArtResponse>(
    deletePromoArt.definition,
  );
  // TODO: check if we use it ask gabi or paweł
  const [controlMutation] = useMutation(UPDATE_CONTROL_PROMO_ART);
  const [updateReferenceMutation] = useMutation<
    UpdateReferenceVariationResponse
  >(updateReferenceVariationMutation.definition);
  const [deactivateMutation] = useMutation<DeactivatePromoArtResponse>(
    deactivatePromoArt.definition,
  );

  const setModal = useContext(ModalContext);
  const applicationCtx = useContext(ApplicationContext);

  const refetchQuery = {
    query: GET_APPLICATION_PROMO_ARTS,
    variables: { applicationId },
  };

  const trafficIndicator =
    applicationCtx.current &&
    data &&
    applicationCtx.current.status === Status.active
      ? getTrafficIndicator(data.phaseTraffic, data.trafficNeeded)
      : undefined;

  const removeAsset = React.useCallback(
    (asset: ApplicationAsset) => async () => {
      await runAction(
        applicationId,
        asset,
        deleteMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    },
    [applicationId, deleteMutation, setModal, refetchQuery, setFullPageLoader],
  );

  const displayRemoveModal = React.useMemo(() => {
    if (!hasUserPermissions(deletePromoArt.requiredPermissions)) {
      return null;
    }

    return (asset: ApplicationAsset) => {
      setModal({
        type: "set",
        props: {
          title: "Delete",
          content: "Are you sure you want to delete this asset?",
          onCancel: () => {},
          onConfirm: removeAsset(asset),
        },
      });
    };
  }, [hasUserPermissions, removeAsset, setModal]);

  const deactivateAsset = React.useCallback(
    (asset: ApplicationAsset) => async () => {
      await runAction(
        applicationId,
        asset,
        deactivateMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    },
    [
      applicationId,
      deactivateMutation,
      setModal,
      refetchQuery,
      setFullPageLoader,
    ],
  );

  const displayDeactivateModal = React.useMemo(() => {
    if (!hasUserPermissions(deactivatePromoArt.requiredPermissions)) {
      return null;
    }

    return (asset: ApplicationAsset) => {
      setModal({
        type: "set",
        props: {
          title: "Remove Asset From Test",
          content:
            "Are you sure you want to remove this asset from current test?",
          onCancel: () => {},
          onConfirm: deactivateAsset(asset),
        },
      });
    };
  }, [hasUserPermissions, setModal, deactivateAsset]);

  const onControl = async (asset: ApplicationAsset) => {
    await runAction(
      applicationId,
      asset,
      controlMutation,
      setModal,
      refetchQuery,
      setFullPageLoader,
    );
  };

  const onSetTop = React.useMemo(() => {
    if (
      !hasUserPermissions(updateReferenceVariationMutation.requiredPermissions)
    ) {
      return null;
    }

    return async (asset: ApplicationAsset) => {
      await updateReferenceVariation(
        applicationId,
        asset,
        AssetType.promoArt,
        updateReferenceMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    };
  }, [
    refetchQuery,
    setFullPageLoader,
    setModal,
    updateReferenceMutation,
    applicationId,
    hasUserPermissions,
  ]);

  if (loading || !data) {
    return <Spinner />;
  }

  if (error) {
    return <Error errorElement={error.message} />;
  }

  return (
    <ApplicationElements
      applicationId={applicationId}
      history={history}
      uploadRoute={authRoutes.APPLICATION_CREATE_PROMO_ARTS.getUrlWithParams({
        id: applicationId,
      })}
      permissionsForUpload={createPromoArts.requiredPermissions}
    >
      {fullPageLoader}
      <Page
        applicationId={applicationId}
        currentPhase={data.currentPhase}
        assets={data.promoArts}
        onControl={onControl}
        onRemove={displayRemoveModal}
        onDeactivate={displayDeactivateModal}
        onSetTop={onSetTop}
        history={history}
        trafficIndicator={trafficIndicator}
        currentIteration={data.currentIteration}
      />
    </ApplicationElements>
  );
}

export default ApplicationElementsPromoArtsContainer;
